// src/context/ChartPaletteContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Global chart color palette — 9 curated presets + custom brand palette.
// Every chart/graph in the app reads colors from this context.
// Persisted to localStorage so the choice survives reloads.
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { syncPalette } from './paletteSync';

const STORAGE_KEY = 'jorvex:chartPalette';
const CUSTOM_STORAGE_KEY = 'jorvex:chartPaletteCustom';

// ── 9 Curated Palettes ─────────────────────────────────────────────────────
export const PALETTE_PRESETS = [
  {
    id: 'default',
    name: 'Jorvex',
    description: 'Default neon-on-dark palette',
    colors: ['#22d3ee','#4ade80','#a78bfa','#fbbf24','#fb923c','#f87171','#f472b6','#60a5fa','#34d399','#67e8f9'],
  },
  {
    id: 'ocean',
    name: 'Deep Ocean',
    description: 'Cool blues and teals',
    colors: ['#06b6d4','#0ea5e9','#3b82f6','#6366f1','#818cf8','#38bdf8','#22d3ee','#67e8f9','#7dd3fc','#a5f3fc'],
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Warm oranges, reds, and pinks',
    colors: ['#f97316','#ef4444','#ec4899','#f59e0b','#fb923c','#f87171','#f472b6','#fbbf24','#fca5a5','#fdba74'],
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Natural greens and earth tones',
    colors: ['#22c55e','#16a34a','#84cc16','#65a30d','#a3e635','#4ade80','#86efac','#fbbf24','#a78bfa','#34d399'],
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'High-contrast electric colors',
    colors: ['#00ff87','#00d9ff','#ff3d71','#ffaa00','#bf5af2','#32d74b','#ff6482','#64d2ff','#ffd60a','#ff375f'],
  },
  {
    id: 'pastel',
    name: 'Pastel',
    description: 'Soft muted tones',
    colors: ['#93c5fd','#86efac','#c4b5fd','#fde68a','#fdba74','#fca5a5','#f9a8d4','#67e8f9','#a7f3d0','#ddd6fe'],
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Professional blues and grays',
    colors: ['#2563eb','#3b82f6','#6366f1','#8b5cf6','#60a5fa','#1d4ed8','#4f46e5','#7c3aed','#93c5fd','#a78bfa'],
  },
  {
    id: 'retro',
    name: 'Retro',
    description: 'Vintage warm tones',
    colors: ['#e07a5f','#3d405b','#81b29a','#f2cc8f','#f4f1de','#d4a373','#ccd5ae','#e9edc9','#fefae0','#faedcd'],
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Electric pink and cyan',
    colors: ['#ff00ff','#00ffff','#ff3366','#33ff99','#ffff00','#ff6600','#6633ff','#00ff66','#ff0099','#0066ff'],
  },
];

const ChartPaletteContext = createContext(null);

function loadSaved() {
  try {
    const id = localStorage.getItem(STORAGE_KEY);
    if (id === 'custom') {
      const raw = localStorage.getItem(CUSTOM_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length >= 2) return { id: 'custom', colors: parsed };
      }
    }
    if (id) {
      const preset = PALETTE_PRESETS.find(p => p.id === id);
      if (preset) return { id: preset.id, colors: preset.colors };
    }
  } catch (_) { /* ignore */ }
  return { id: 'default', colors: PALETTE_PRESETS[0].colors };
}

export function ChartPaletteProvider({ children }) {
  const [state, setState] = useState(loadSaved);

  // Sync to mutable array so wsShared PALETTE stays in sync
  useEffect(() => { syncPalette(state.colors); }, [state.colors]);

  const setPalette = useCallback((id) => {
    const preset = PALETTE_PRESETS.find(p => p.id === id);
    if (!preset) return;
    localStorage.setItem(STORAGE_KEY, id);
    setState({ id: preset.id, colors: [...preset.colors] });
  }, []);

  const setCustomPalette = useCallback((colors) => {
    if (!Array.isArray(colors) || colors.length < 2) return;
    // Pad to 10 if fewer provided
    const padded = [...colors];
    while (padded.length < 10) padded.push(padded[padded.length - 1]);
    localStorage.setItem(STORAGE_KEY, 'custom');
    localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(padded.slice(0, 10)));
    setState({ id: 'custom', colors: padded.slice(0, 10) });
  }, []);

  const value = useMemo(() => ({
    paletteId: state.id,
    colors: state.colors,
    setPalette,
    setCustomPalette,
  }), [state, setPalette, setCustomPalette]);

  return (
    <ChartPaletteContext.Provider value={value}>
      {children}
    </ChartPaletteContext.Provider>
  );
}

export function useChartPalette() {
  const ctx = useContext(ChartPaletteContext);
  if (!ctx) throw new Error('useChartPalette must be inside ChartPaletteProvider');
  return ctx;
}

// ── Singleton for non-React code (wsShared, workers, etc.) ─────────────────
// This is updated by the provider on every palette change so that
// imported PALETTE arrays in wsShared.js always reflect the current choice.
let _liveColors = loadSaved().colors;
export function _setLiveColors(c) { _liveColors = c; }
export function getLivePalette() { return _liveColors; }
